import {useContext, useEffect, useMemo, useState} from 'preact/hooks';

import {deleteGroupDraft, loadAdminState} from '../lib/api.js';
import {LanguageContext, useTranslate} from '../lib/i18n.js';

const PAGE_SIZE = 20;

export default function GroupsPage() {
  const t = useTranslate();
  const {toggleLanguage} = useContext(LanguageContext);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [busyId, setBusyId] = useState('');
  const [confirmId, setConfirmId] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => { load(); }, []);

  async function load() {
    setError('');
    try {
      setData(await loadAdminState());
    } catch (loadError) {
      setError(message(loadError));
    }
  }

  async function remove(groupId) {
    if (confirmId !== groupId) {
      setConfirmId(groupId);
      shopify.toast.show(t('confirmDelete'));
      return;
    }
    setBusyId(groupId);
    try {
      const state = await deleteGroupDraft(groupId);
      setData((current) => ({...current, state}));
      setConfirmId('');
      shopify.toast.show(t('deleted'));
    } catch (deleteError) {
      setError(message(deleteError));
    } finally {
      setBusyId('');
    }
  }

  const groups = data?.state.groups || [];
  const pages = Math.max(1, Math.ceil(groups.length / PAGE_SIZE));
  useEffect(() => {
    if (page > pages) setPage(pages);
  }, [page, pages]);
  const visibleGroups = useMemo(
    () => groups.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE),
    [groups, page],
  );

  return (
    <s-page heading={t('groups')}>
      <s-button slot="primary-action" variant="primary" href="/groups/new">{t('createGroup')}</s-button>
      <s-button slot="secondary-actions" onClick={toggleLanguage}>{t('language')}</s-button>

      {data?.shop && (
        <s-section>
          <s-stack direction="inline" gap="small-200">
            <s-text type="strong">{t('store')}:</s-text>
            <s-text>{data.shop.name} · {data.shop.myshopifyDomain}</s-text>
          </s-stack>
        </s-section>
      )}

      {error && <s-banner tone="critical">{t('loadFailed')}: {error}</s-banner>}
      {!data && !error && <s-section><s-paragraph>{t('loading')}</s-paragraph></s-section>}
      {!data && error && <s-button onClick={load}>{t('retry')}</s-button>}

      {data && groups.length === 0 && (
        <s-section>
          <s-grid gap="base" justifyItems="center" paddingBlock="large-400">
            <s-heading>{t('noGroups')}</s-heading>
            <s-paragraph>{t('noGroupsHelp')}</s-paragraph>
            <s-button href="/groups/new" variant="primary">{t('createGroup')}</s-button>
          </s-grid>
        </s-section>
      )}

      {data && visibleGroups.length > 0 && (
        <s-section padding="none">
          <s-table>
            <s-table-header-row>
              <s-table-header listSlot="primary">{t('groupName')}</s-table-header>
              <s-table-header>{t('status')}</s-table-header>
              <s-table-header>{t('mainProducts')}</s-table-header>
              <s-table-header>{t('accessories')}</s-table-header>
              <s-table-header></s-table-header>
            </s-table-header-row>
            <s-table-body>
              {visibleGroups.map((group) => (
                <s-table-row key={group.id}>
                  <s-table-cell>
                    <s-link href={`/groups/${group.id}`}><s-text type="strong">{group.name || t('noGroups')}</s-text></s-link>
                  </s-table-cell>
                  <s-table-cell>
                    <s-badge tone={group.status === 'PUBLISHED' ? 'success' : 'info'}>
                      {group.status === 'PUBLISHED' ? t('published') : t('draft')}
                    </s-badge>
                  </s-table-cell>
                  <s-table-cell>{group.assignments.length}</s-table-cell>
                  <s-table-cell>{group.items.length}</s-table-cell>
                  <s-table-cell>
                    <s-button href={`/groups/${group.id}`} variant="tertiary">{t('edit')}</s-button>
                    <s-button variant="tertiary" tone="critical" loading={busyId === group.id} onClick={() => remove(group.id)}>
                      {confirmId === group.id ? t('confirmDelete') : t('delete')}
                    </s-button>
                  </s-table-cell>
                </s-table-row>
              ))}
            </s-table-body>
          </s-table>
        </s-section>
      )}

      {groups.length > PAGE_SIZE && (
        <s-section>
          <s-stack direction="inline" justifyContent="center" alignItems="center" gap="base">
            <s-button disabled={page <= 1} onClick={() => setPage((value) => Math.max(1, value - 1))}>{t('previous')}</s-button>
            <s-text>{t('page')} {page} / {pages}</s-text>
            <s-button disabled={page >= pages} onClick={() => setPage((value) => Math.min(pages, value + 1))}>{t('next')}</s-button>
          </s-stack>
        </s-section>
      )}
    </s-page>
  );
}

function message(error) {
  return error instanceof Error ? error.message : String(error);
}
